import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Copy, ArrowRight, CheckCircle2 } from "lucide-react";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import SEO from "@/components/storefront/SEO";
import { toast } from "sonner";

export default function AccountForgot() {
  const { requestPasswordReset } = useCustomerAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await requestPasswordReset(email);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setDone(res);
  };

  const copyToken = async () => {
    if (!done?.debug_token) return;
    try {
      await navigator.clipboard.writeText(done.debug_token);
      toast.success("Reset code copied");
    } catch {
      toast.error("Couldn't copy — please select manually");
    }
  };

  return (
    <div className="mx-auto grid min-h-[70vh] max-w-md px-6 py-14" data-testid="account-forgot-page">
      <SEO title="Forgot password" noindex />
      <div className="m-auto w-full">
        <div className="mb-2 text-xs uppercase tracking-[0.3em] text-[#6B5158]">Account</div>
        <h1 className="font-display text-5xl leading-tight md:text-[60px]">Forgot your <span className="font-display-soft">password?</span></h1>
        <p className="mt-3 text-sm text-[#6B5158]">Enter your email and we'll generate a one-time reset code.</p>

        {!done ? (
          <form onSubmit={submit} className="mt-10 space-y-4">
            <label className="block">
              <span className="mb-1 block text-[10px] uppercase tracking-[0.2em] text-[#6B5158]">Email</span>
              <span className="relative block">
                <Mail size={14} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#6B5158]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full rounded-full border border-[#E6D4CC] bg-white py-3 pl-11 pr-4 text-sm outline-none focus:border-[#2B2127]"
                  data-testid="forgot-email"
                />
              </span>
            </label>

            {error && <div className="rounded-md bg-rose-50 p-3 text-xs text-rose-700" data-testid="forgot-error">{error}</div>}

            <button
              type="submit"
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#2B2127] px-6 py-3.5 text-sm text-white transition-colors hover:bg-[#9D5C6C] disabled:opacity-60"
              data-testid="forgot-submit"
            >
              {busy ? "Generating…" : (<>Send reset code <ArrowRight size={14} /></>)}
            </button>

            <p className="text-center text-xs text-[#6B5158]">
              Remembered it?{" "}
              <Link to="/account/login" className="underline decoration-[#9D5C6C] underline-offset-4 hover:text-[#2B2127]">Sign in</Link>
            </p>
          </form>
        ) : (
          <div className="mt-10 rounded-md border border-[#E6D4CC] bg-white p-6" data-testid="forgot-success">
            <div className="flex items-center gap-2 text-[#9D5C6C]">
              <CheckCircle2 size={18} />
              <span className="font-display text-lg text-[#2B2127]">Check your email</span>
            </div>
            <p className="mt-2 text-sm text-[#6B5158]">{done.message}</p>

            {done.debug_token && (
              <div className="mt-4 rounded-md bg-[#F1E3DC] p-4 text-xs">
                <div className="mb-1 text-[10px] uppercase tracking-[0.2em] text-[#6B5158]">
                  Demo mode — reset code
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 break-all rounded bg-white px-3 py-2 font-mono text-[11px]" data-testid="forgot-debug-token">{done.debug_token}</code>
                  <button onClick={copyToken} className="flex h-9 w-9 items-center justify-center rounded-full border border-[#E6D4CC] bg-white text-[#6B5158] hover:bg-[#FAF6F1]" aria-label="Copy" data-testid="forgot-copy-btn">
                    <Copy size={14} />
                  </button>
                </div>
                <p className="mt-2 text-[#6B5158]">In production this would arrive in your inbox; for the demo it's shown here.</p>
              </div>
            )}

            <button
              onClick={() => nav(`/account/reset?token=${encodeURIComponent(done.debug_token || "")}`)}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#2B2127] px-6 py-3 text-sm text-white transition-colors hover:bg-[#9D5C6C]"
              data-testid="forgot-continue-btn"
            >
              Continue to reset <ArrowRight size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
